<template>
  <EnterpriseLayout>
    <div class="min-h-screen flex items-center justify-center px-2.5 py-4">
      <div class="w-full max-w-[820px]">
        <section
          class="relative rounded-[28px] p-6 md:p-10 overflow-hidden bg-gradient-to-br from-[#1c1f26] to-[#181b21] border border-white/10 shadow-[0_0_55px_rgba(0,0,0,0.65)]"
        >
          <!-- glow vermelho (canto inferior direito) -->
          <div
            class="absolute -bottom-20 -right-20 w-[360px] h-[260px] blur-3xl rounded-full pointer-events-none"
            :class="[isAllAnswered ? 'bg-[#22b558]/25' : 'bg-red-500/25']"
          ></div>

          <!-- header top (sem menu) -->
          <div
            v-if="step === 'intro'"
            class="relative flex items-center justify-between"
          >
            <div class="flex items-center gap-3">
              <span class="w-2.5 h-2.5 rounded-full bg-red-500"></span>
              <p class="text-sm text-gray-300">Antes de começar</p>
            </div>

            <!-- botão voltar só no kiosk -->
            <button
              v-if="mode === 'kiosk' && step === 'intro'"
              type="button"
              class="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 transition text-sm"
              @click="goBackToQr"
              title="Voltar"
            >
              ← Voltar
            </button>
          </div>

          <!-- CONTENT -->
          <div v-if="step === 'intro'" class="relative mt-6 text-start">
            <h1
              class="text-4xl md:text-5xl font-semibold text-white leading-tight"
            >
              Antes de começar
            </h1>

            <p class="mt-3 text-sm md:text-base text-[#70e188] font-medium">
              Leia as instruções antes de preencher o questionário
            </p>

            <div class="mt-6 h-px bg-white/10"></div>

            <p class="mt-6 text-sm md:text-base text-gray-300">
              <span class="text-gray-200 font-medium">{{
                questionnaireName
              }}</span>
              — Questionário de auto relato
            </p>

            <!-- SQR21 -->
            <p
              v-if="responseMode == 'scale_1_5'"
              class="mt-3 text-sm md:text-base text-[#70e188] font-medium"
            >
              Leia cada pergunta com calma e escolha a opção que mais combina
              com a sua realidade. Não existe resposta certa ou errada.
            </p>
            <div
              v-if="responseMode == 'scale_1_5'"
              class="mt-4 rounded-xl bg-white/5 border border-white/10 p-4 text-sm md:text-base text-gray-200 leading-relaxed"
            >
              <p class="text-gray-200 font-medium mb-3">Escala de resposta:</p>

              <p>
                <span class="text-[#70e188] font-semibold">1</span> = Nunca ou
                quase nunca
              </p>
              <p>
                <span class="text-[#70e188] font-semibold">2</span> = Raramente
              </p>
              <p>
                <span class="text-[#70e188] font-semibold">3</span> = Às vezes
              </p>
              <p>
                <span class="text-[#70e188] font-semibold">4</span> =
                Frequentemente
              </p>
              <p>
                <span class="text-[#70e188] font-semibold">5</span> = Sempre
              </p>
            </div>

            <!-- SQR20 -->
            <p
              v-else
              class="mt-3 text-sm md:text-base text-[#70e188] font-medium"
            >
              Marque <span class="text-white">SIM</span> se a questão se aplica
              a você e você teve o problema. Marque
              <span class="text-white">NÃO</span> se não se aplica.
            </p>

            <div class="mt-6 h-px bg-white/10"></div>

            <p class="mt-6 text-sm text-gray-400 leading-relaxed">
              Responda com base nos
              <span class="text-gray-200 font-medium">últimos 30 dias</span>. O
              diagnóstico definitivo só pode ser fornecido por um profissional.
            </p>

            <!-- setor (kiosk apenas) -->
            <div v-if="mode === 'kiosk'" class="mt-8">
              <div
                class="rounded-xl bg-white/5 border border-white/10 px-4 py-3"
              >
                <label class="block text-xs text-gray-400 mb-2">Função</label>

                <select
                  v-model="selectedRoleId"
                  class="w-full bg-transparent text-white outline-none"
                >
                  <option
                    disabled
                    :value="null"
                    class="bg-[#1c1f26] text-white"
                  >
                    Selecione uma função
                  </option>
                  <option
                    v-for="role in sortedAvailableRoles"
                    :key="role.id"
                    :value="role.id"
                    class="bg-[#1c1f26] text-white"
                  >
                    {{ role.name }}
                  </option>
                </select>
              </div>

              <p v-if="error" class="text-xs text-red-400 mt-2">
                {{ error }}
              </p>
            </div>

            <!-- setor read-only (collaborator) -->
            <div v-else class="mt-8">
              <div
                class="rounded-xl bg-white/5 border border-white/10 px-4 py-3"
              >
                <p class="text-xs text-gray-400">Função</p>
                <p class="text-sm text-white font-medium mt-1">
                  {{ sector || "—" }}
                </p>
              </div>
            </div>

            <!-- CTA -->
            <button
              type="button"
              class="mt-10 w-full py-4 rounded-full bg-gradient-to-br from-[#22b558] to-[#22c55e] hover:bg-green-200 hover:scale-[1.02] transition-all hover:shadow-[0_0_10px_#22b558] text-white font-semibold text-lg"
              @click="start"
            >
              Iniciar questionário
            </button>

            <!-- nota -->
            <p class="mt-4 text-xs text-gray-200">
              * Responda com sinceridade. Não há respostas certas ou erradas.
            </p>
          </div>

          <!-- QUESTIONS -->
          <div v-else-if="step === 'questions'" class="relative text-gray-300">
            <Transition name="qslide" mode="out-in">
              <Srq20Flow
                :questions="questions"
                :response-mode="responseMode"
                :options="questionnaireOptions"
                :questionnaire-name="questionnaireName"
                @request-submit="openConfirm"
                @status="onStatus"
                :key="flowKey"
              />
            </Transition>

            <ConfirmModal
              :open="confirmOpen"
              :response-mode="responseMode"
              :yes-count="yesCount"
              :no-count="noCount"
              :answered-count="answeredCount"
              :total="totalCount"
              title="Confirmar envio"
              description="Revise e confirme o envio do questionário."
              @cancel="confirmOpen = false"
              @confirm="confirmSubmit"
            />

            <p v-if="error" class="mt-4 text-sm text-red-400 text-center">
              {{ error }}
            </p>

            <!-- <div class="mt-6 flex gap-3">
              <button
                class="px-4 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition"
                @click="backToIntro"
              >
                ← Voltar
              </button>
              <button
                class="px-4 py-2 rounded-lg bg-green-500/90 hover:bg-green-500 transition text-white"
                @click="finishMock"
              >
                (Mock) Finalizar
              </button>
            </div> -->
          </div>

          <!-- DONE (para colaborador) -->
          <div v-else class="relative mt-10 text-center">
            <h2 class="text-2xl font-semibold text-white">Obrigado!</h2>
            <p class="text-gray-400 mt-3">
              Seu questionário foi registrado com sucesso.
            </p>
            <p class="text-xs text-gray-500 mt-4">
              Este dispositivo já concluiu a avaliação.
            </p>

            <!-- kiosk pode voltar ao início -->
            <button
              v-if="mode === 'kiosk'"
              class="mt-8 px-5 py-3 rounded-full bg-gradient-to-br from-[#22b558] to-[#22c55e] hover:bg-green-200 hover:scale-[1.01] transition-all hover:shadow-[0_0_10px_#22b558] text-white font-medium"
              @click="resetKiosk"
            >
              Iniciar nova resposta
            </button>
          </div>
        </section>
      </div>
    </div>
  </EnterpriseLayout>
</template>

<script setup>
import Srq20Flow from "@/components/srq20/Srq20Flow.vue";
import EnterpriseLayout from "@/layouts/EnterpriseLayout.vue";
import ConfirmModal from "@/components/ui/ConfirmModal.vue";
import router from "@/router";
import { ref, onMounted, computed, defineProps } from "vue";
import { submitQuestionnaire } from "@/services/public/publicFormService";
import {
  getCollaboratorInviteContext,
  getCollaboratorRoleContext,
  getCollaboratorDoneKey,
} from "@/services/public/collaboratorSessionService";
import { getPortalKioskContext } from "@/services/portal/portalSessionService";

const props = defineProps({
  mode: {
    type: String,
    default: "kiosk",
  },
});

const mode = ref(props.mode);
const step = ref("intro");
const isAllAnswered = ref(false);
const flowKey = ref(0);

const sector = ref("");
const error = ref("");

const confirmOpen = ref(false);
const pendingPayload = ref(null);

const inviteContext = ref(null);
const roleContext = ref(null);

const kioskContext = ref(null);
const availableRoles = ref([]);
const selectedRoleId = ref(null);
const questions = ref([]);

const responseMode = ref("binary");
const questionnaireOptions = ref([]);
const questionnaireName = ref("Questionário");

const totalCount = computed(() => pendingPayload.value?.answers?.length || 0);

const answeredCount = computed(
  () =>
    (pendingPayload.value?.answers || []).filter(
      (a) => a.answer_value !== null && a.answer_value !== undefined
    ).length
);

const yesCount = computed(() => {
  if (responseMode.value !== "binary") return 0;
  return (pendingPayload.value?.answers || []).filter(
    (a) => a.answer_value === 1
  ).length;
});

const noCount = computed(() => {
  if (responseMode.value !== "binary") return 0;
  return (pendingPayload.value?.answers || []).filter(
    (a) => a.answer_value === 0
  ).length;
});

const sortedAvailableRoles = computed(() => {
  return [...availableRoles.value].sort((a, b) => {
    return (a.name || '').localeCompare(b.name || '', 'pt-BR', {
      sensitivity: 'base'
    })
  })
})

onMounted(() => {
  if (mode.value === "collaborator") {
    inviteContext.value = getCollaboratorInviteContext();
    roleContext.value = getCollaboratorRoleContext();

    if (!inviteContext.value) {
      router.replace({ name: "login-collab" });
      return;
    }

    // const doneKey = getCollaboratorDoneKey(inviteContext.value.invite_token);
    // const done = localStorage.getItem(doneKey) === "1";

    const doneKey = getCollaboratorDoneKey(
      inviteContext.value?.company?.id,
      inviteContext.value?.questionnaire?.version_id
    );

    const done = localStorage.getItem(doneKey) === "1";

    if (done) {
      step.value = "done";
      return;
    }

    if (!roleContext.value) {
      router.replace({ name: "login-collab" });
      return;
    }

    sector.value = roleContext.value.role_name || "";

    const questionnaire = inviteContext.value.questionnaire || {};
    questions.value = questionnaire.questions || [];
    responseMode.value = questionnaire.response_mode || "binary";
    questionnaireOptions.value = questionnaire.options || [];
    questionnaireName.value = questionnaire.name || "Questionário";

    return;
  }

  kioskContext.value = getPortalKioskContext();

  if (!kioskContext.value) {
    router.replace({ name: "enter" });
    return;
  }

  availableRoles.value = kioskContext.value.roles || [];

  const questionnaire = kioskContext.value.questionnaire || {};
  questions.value = questionnaire.questions || [];
  responseMode.value = questionnaire.response_mode || "binary";
  questionnaireOptions.value = questionnaire.options || [];
  questionnaireName.value = questionnaire.name || "Questionário";
});

function goBackToQr() {
  router.push({ name: "enter" }); // /enterprises
}

function start() {
  error.value = "";

  if (mode.value === "kiosk" && !selectedRoleId.value) {
    error.value = "Informe o setor para continuar.";
    return;
  }

  step.value = "questions";
}

function resetKiosk() {
  step.value = "intro";
  isAllAnswered.value = false;
  error.value = "";
  selectedRoleId.value = null;
  pendingPayload.value = null;
  confirmOpen.value = false;
  flowKey.value++;
}

function openConfirm(payload) {
  pendingPayload.value = payload;
  confirmOpen.value = true;
  // console.log(confirmOpen)
}

function onStatus(payload) {
  isAllAnswered.value = payload.isAllAnswered;
}

// send
async function confirmSubmit() {
  confirmOpen.value = false;
  await finishQuestionnaire(pendingPayload.value);
}

async function finishQuestionnaire(payload) {
  try {
    if (mode.value === "collaborator") {
      const body = {
        invite_token: inviteContext.value?.invite_token,
        role_id: roleContext.value?.role_id,
        channel: "collaborator",
        device_id: roleContext.value?.device_id,
        answers: payload.answers,
      };

      await submitQuestionnaire(body);

      const doneKey = getCollaboratorDoneKey(
        inviteContext.value?.company?.id,
        inviteContext.value?.questionnaire?.version_id
      );

      localStorage.setItem(doneKey, "1");

      step.value = "done";
      return;
    }

    // KIOSK
    const body = {
      invite_token: kioskContext.value?.invite_token,
      role_id: Number(selectedRoleId.value),
      channel: "kiosk",
      answers: payload.answers,
    };

    await submitQuestionnaire(body);

    step.value = "done";
  } catch (e) {
    if (e?.response?.status === 409 && mode.value === "collaborator") {
      const doneKey = getCollaboratorDoneKey(
        inviteContext.value?.company?.id,
        inviteContext.value?.questionnaire?.version_id
      );

      localStorage.setItem(doneKey, "1");
      step.value = "done";
      return;
    }

    error.value =
      e?.response?.data?.message || "Não foi possível enviar o questionário.";
  }
}
</script>
